<template>
  <v-dialog :value="value" max-width="620" persistent @keydown.esc="close">
    <v-card v-if="current">
      <v-card-title class="text-h6">{{ $t('onboarding.badge-designation.title.handover', { person: personName(current) }) }}</v-card-title>

      <!-- 1. Les badges remis : chacun saisi, vérifié, puis validé (enregistré sur la commande) -->
      <v-card-text v-if="!confirming">
        <p class="text-body-2">{{ $t('onboarding.badge-designation.explained.handover') }}</p>
        <v-alert v-if="error" type="error" dense outlined class="mb-3">{{ error }}</v-alert>
        <badge-entry
            v-for="kind in kinds" :key="kind" :order-id="current.id" :kind="kind"
            :label="$t(`onboarding.commissioning.badge-supply.${kind}`)" :held="held[kind] || ''"
            :held-by="current.badgesHeldBy" :held-at="current.badgesHeldAt" class="mb-4"
            @held="onHeld" @dirty="dirty = { ...dirty, [kind]: $event }" @verdict="verdicts = { ...verdicts, [kind]: $event }"
        />
        <p class="text-caption text--secondary mt-1 mb-0">{{ $t('onboarding.badge-designation.holder-activates') }}</p>
      </v-card-text>

      <!-- 2. Un badge déjà à quelqu'un, ou d'une autre organisation : on le redit avant de remettre -->
      <v-card-text v-else>
        <v-alert v-for="warning in warnings" :key="warning.kind" type="warning" text dense class="text-body-2">{{ warning.text }}</v-alert>
        <p class="text-body-2 mb-0">{{ $t('onboarding.badge-designation.continue-question') }}</p>
      </v-card-text>

      <v-card-actions class="px-6 pb-4">
        <span v-if="!confirming && heldCount && anyDirty" class="text-caption text--secondary">{{ $t('onboarding.badge-entry.kept-for-later') }}</span>
        <v-spacer />
        <v-btn small text @click="confirming ? (confirming = false) : close()">{{ $t(confirming ? 'back' : 'close') }}</v-btn>
        <v-btn small color="primary" :loading="working" :disabled="!complete" @click="submit">
          {{ $t(confirming ? 'onboarding.badge-designation.continue' : 'onboarding.badge-designation.confirm.handover') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CommissioningRepository from '@repository/CommissioningRepository'
import CommissioningMixin from '../CommissioningMixin'
import BadgeEntry from './BadgeEntry.vue'

/**
 * Le gestionnaire de flotte remet les badges en main propre et dit lesquels : chaque numéro (imprimé, ou l'UID) se saisit
 * puis se valide — enregistré aussitôt sur la commande, il ne se perd pas si l'on ferme, et nul autre ne le prend. La
 * remise se valide quand tous les badges commandés sont enregistrés : ils sortent du stock sans être activés — c'est la
 * personne qui les active, dans l'application ou son espace client. Fermer avec une frappe non validée demande
 * confirmation.
 */
export default {
  name: 'BadgeDesignationDialog',

  components: { BadgeEntry },

  mixins: [CommissioningMixin],

  props: {
    value: { type: Boolean, default: false },
    order: { type: Object, default: null },
  },

  data() {
    return { current: null, verdicts: {}, dirty: {}, confirming: false, working: false, error: null }
  },

  computed: {
    kinds() {
      const ordered = this.current?.orderedBadgeKinds
      if (ordered && ordered.length) return ordered
      const supply = this.current?.options?.supply || 'professional'
      return supply === 'both' ? ['professional', 'personal'] : [supply]
    },
    held() {
      return this.current?.designatedBadges || {}
    },
    heldCount() {
      return this.kinds.filter(k => (this.held[k] || '').trim()).length
    },
    anyDirty() {
      return Object.values(this.dirty).some(Boolean)
    },
    /** Noter les numéros est facultatif : seuls ceux notés doivent être bons, les autres sont remis sans numéro. */
    complete() {
      return !this.anyDirty
        && this.kinds.filter(k => (this.held[k] || '').trim()).every(k => this.verdicts[k] && (this.verdicts[k].status === 'free' || this.verdicts[k].needsConfirmation))
    },
    warnings() {
      return this.kinds.filter(k => this.verdicts[k]?.needsConfirmation).map(kind => ({ kind, text: this.warningOf(kind) }))
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) Object.assign(this, { current: this.order, verdicts: {}, dirty: {}, confirming: false, working: false, error: null })
      },
    },
  },

  methods: {
    warningOf(kind) {
      const v = this.verdicts[kind]
      return this.$t(`onboarding.badge-check.warning.${v.status}`, { badge: this.held[kind], organization: v.organizationName || '-' })
    },

    /** Un badge enregistré ou retiré : la commande à jour ici, et dans la liste derrière. */
    onHeld({ order }) {
      this.current = order
      this.$emit('updated', order)
    },

    async close() {
      if (this.anyDirty && !(await this.$dialog.confirm({
        icon: 'mdi-alert', type: 'warning', persistent: true,
        title: this.$t('confirm-unsaved-changes-title'), text: this.$t('onboarding.badge-entry.close-unsaved'),
      }))) return
      this.$emit('input', false)
    },

    async submit() {
      if (!this.complete || this.working) return
      if (!this.confirming && this.warnings.length) {
        this.confirming = true
        return
      }
      this.working = true
      this.error = null
      try {
        // Ce qui est remis est ce qui est enregistré sur la commande.
        const result = await CommissioningRepository.handOverBadges(this.current.id, { confirmed: this.warnings.length > 0 })
        this.$emit('done', result)
        this.$emit('input', false)
      } catch (e) {
        this.confirming = false
        this.error = this.messageOf(e)
      } finally {
        this.working = false
      }
    },
  },
}
</script>
